import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CalendarDays, CheckCircle2, Circle, Loader2, Pencil } from 'lucide-react';
import BackfillDayModal from './BackfillDayModal';
import { toast } from 'sonner';
import { formatLocalDate } from '../utils/dateHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEKDAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function lastNDates(n) {
  const out = [];
  const today = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    out.push({
      date: formatLocalDate(d),
      weekday: WEEKDAY_LETTERS[d.getDay()],
      dayNum: d.getDate(),
      isToday: i === 0,
    });
  }
  return out;
}

/**
 * Catch-up strip showing the last 7 days. Each day has:
 *   • a status pill (✓ logged / · partial / empty)
 *   • a one-tap "Took everything" button (skips the full modal)
 *   • a pencil icon to open the full BackfillDayModal for fine-grained edits
 *
 * Hidden if the user has no active reminders (nothing to backfill).
 */
export default function WeekBackfillStrip({ getAuthHeader }) {
  const [days] = useState(() => lastNDates(7));
  const [supplementList, setSupplementList] = useState([]);
  const [dayState, setDayState] = useState({}); // { 'YYYY-MM-DD': { logged: bool, count: N } }
  const [loading, setLoading] = useState(true);
  const [savingDate, setSavingDate] = useState(null);
  const [editingDate, setEditingDate] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const remindersRes = await axios.get(`${API}/reminders`, { headers: getAuthHeader() });
      const reminders = Array.isArray(remindersRes.data) ? remindersRes.data : [];
      const seen = new Set();
      const names = [];
      reminders.forEach(r => {
        if (r.enabled === false) return;
        if (r.is_medication) return;
        const raw = (r.supplement_name || '').trim();
        if (!raw) return;
        if (raw.startsWith('💧') || /water \(cup/i.test(raw)) return;
        const key = raw.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        names.push(raw);
      });
      setSupplementList(names);

      const dayResults = await Promise.all(
        days.map(d =>
          axios.get(`${API}/checkin/day/${d.date}`, { headers: getAuthHeader() })
            .then(r => [d.date, r.data])
            .catch(() => [d.date, { checked_in: false, supplements_taken: [] }])
        )
      );
      const next = {};
      dayResults.forEach(([date, data]) => {
        next[date] = {
          logged: !!data.checked_in,
          count: (data.supplements_taken || []).length,
        };
      });
      setDayState(next);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const tookEverything = async (date) => {
    if (!supplementList.length) return;
    setSavingDate(date);
    try {
      await axios.post(
        `${API}/checkin`,
        { supplements_taken: supplementList, date },
        { headers: getAuthHeader() }
      );
      setDayState(prev => ({
        ...prev,
        [date]: { logged: true, count: supplementList.length },
      }));
      toast.success('Logged everything for that day');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save');
    } finally {
      setSavingDate(null);
    }
  };

  // Hide widget if no active reminders — nothing to backfill against.
  if (!loading && supplementList.length === 0) return null;

  const missedCount = days.filter(d => !dayState[d.date]?.logged).length;
  const total = supplementList.length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-border p-5"
      data-testid="week-backfill-strip"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-[#1A1A1A]">
            <CalendarDays className="w-4 h-4 text-indigo-600" />
            Catch up this week
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {missedCount === 0
              ? 'All caught up — nice work.'
              : `${missedCount} ${missedCount === 1 ? 'day' : 'days'} unlogged · tap "Took all" to backfill in one tap`}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-1.5" data-testid="week-strip-grid">
          {days.map(d => {
            const state = dayState[d.date] || { logged: false, count: 0 };
            const isFullyLogged = state.logged && state.count >= total;
            const isPartial = state.logged && state.count > 0 && state.count < total;
            const isSaving = savingDate === d.date;
            return (
              <div
                key={d.date}
                className={`rounded-xl border p-2 flex flex-col items-center gap-1.5 transition-colors ${
                  isFullyLogged
                    ? 'bg-emerald-50 border-emerald-200'
                    : isPartial
                      ? 'bg-amber-50 border-amber-200'
                      : 'bg-white border-border'
                } ${d.isToday ? 'ring-2 ring-indigo-300' : ''}`}
                data-testid={`week-strip-day-${d.date}`}
              >
                <div className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
                  {d.weekday}
                </div>
                <div className="text-base font-bold text-[#1A1A1A] leading-none">{d.dayNum}</div>
                <div className="h-4 flex items-center justify-center">
                  {isFullyLogged ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  ) : isPartial ? (
                    <span className="text-[10px] font-semibold text-amber-700">{state.count}/{total}</span>
                  ) : (
                    <Circle className="w-3.5 h-3.5 text-muted-foreground/50" />
                  )}
                </div>
                {!isFullyLogged ? (
                  <button
                    type="button"
                    onClick={() => tookEverything(d.date)}
                    disabled={isSaving}
                    className="w-full text-[10px] font-semibold px-1 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 active:scale-95 transition-all disabled:opacity-60"
                    data-testid={`week-strip-took-all-${d.date}`}
                  >
                    {isSaving ? '...' : 'Took all'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setEditingDate(d.date)}
                    className="w-full text-[10px] font-medium px-1 py-1 rounded-md bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-50 transition-colors flex items-center justify-center gap-0.5"
                    data-testid={`week-strip-edit-${d.date}`}
                  >
                    <Pencil className="w-2.5 h-2.5" />
                    Edit
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <BackfillDayModal
        open={!!editingDate}
        date={editingDate}
        onClose={() => setEditingDate(null)}
        onSaved={() => loadAll()}
        getAuthHeader={getAuthHeader}
      />
    </motion.div>
  );
}
